import React, { useState, useEffect } from "react";
import {
  X,
  Package,
  Building2,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";

const SendReceiveSideBar = ({ material, onClose }) => {
  const [sites, setSites] = useState([]);
  const [siteMaterials, setSiteMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [action, setAction] = useState(null); // 'send' or 'receive'
  const [error, setError] = useState("");
  const { addAlert } = useAlert();
  const organizationId = JSON.parse(localStorage.getItem("organization"))?.id;
  const materialId = material?.material?.id;
  const materialData = material?.material;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch sites
      const sitesRes = await fetch(
        `${BASE_URL}/sites/organizations/${organizationId}/sites/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        }
      );

      if (!sitesRes.ok) throw new Error("Failed to fetch sites");
      const sitesData = await sitesRes.json();
      setSites(sitesData);

      // Fetch material availability in sites
      const materialsRes = await fetch(
        `${BASE_URL}/inventory/list-materials-in-organization-sites/${organizationId}/${materialId}/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        }
      );

      if (!materialsRes.ok) throw new Error("Failed to fetch site materials");
      const materialsData = await materialsRes.json();
      setSiteMaterials(materialsData);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const getSiteMaterial = (siteId) => {
    return siteMaterials.find((sm) => sm.sites === siteId);
  };

  const handleAction = (site, actionType) => {
    setSelectedSite(site);
    setAction(actionType);
    setQuantity("");
    setError("");
  };

  const handleSubmit = async () => {
    if (!quantity || parseFloat(quantity) <= 0) {
      setError("Please enter a valid quantity");
      return;
    }

    const siteMaterial = getSiteMaterial(selectedSite.id);

    if (action === "send") {
      // Check if warehouse has enough stock
      if (parseFloat(quantity) > material.current_quantity) {
        setError(
          `Insufficient warehouse stock. Available: ${material.current_quantity} ${materialData.uom}`
        );
        return;
      }
      const res = await fetch(`${BASE_URL}/inventory/send-material-to-site/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
        body: JSON.stringify({
          site_id: selectedSite.id,
          warehouse_id: material.id,
          quantity: parseFloat(quantity),
        }),
      });
      if (!res.ok) {
        setError("Failed to send material. Please try again.");
        return;
      }
      addAlert("Material sent successfully", "success");
    } else if (action === "receive") {
      // Check if site has enough stock
      if (!siteMaterial || parseFloat(quantity) > siteMaterial.current_stock) {
        setError(
          `Insufficient site stock. Available: ${
            siteMaterial ? siteMaterial.current_stock : 0
          } ${materialData.uom}`
        );
        return;
      }
      const res = await fetch(
        `${BASE_URL}/inventory/return-material-to-warehouse/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
          body: JSON.stringify({
            site_id: selectedSite.id,
            warehouse_id: material.id,
            material_id: materialId,
            quantity: parseFloat(quantity),
          }),
        }
      );
      if (!res.ok) {
        setError("Failed to receive material. Please try again.");
        return;
      }
      addAlert("Material received successfully", "success");
    }

    // Reset after submission
    setSelectedSite(null);
    setAction(null);
    setQuantity("");
    setError("");
    fetchData();
    onClose();
  };

  const handleCancel = () => {
    setSelectedSite(null);
    setAction(null);
    setQuantity("");
    setError("");
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end z-50">
      <div className="bg-white h-full w-[480px] shadow-xl flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-[#232323]">
              Send / Receive Material
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              <X size={24} />
            </button>
          </div>

          {/* Material Details */}
          <div className="bg-[#F9F9F9] rounded-lg p-4 space-y-2">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#ECE6F0] flex items-center justify-center flex-shrink-0">
                <Package size={20} className="text-[#3B0069]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-[16px] text-[#232323] truncate">
                  {materialData?.material_name}
                </h3>
                <p className="text-[14px] text-gray-500">
                  {materialData?.brand_name} • {materialData?.subcategory}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <span className="text-[14px] text-gray-500">Warehouse Stock</span>
              <span className="font-semibold text-[16px] text-[#232323]">
                {material?.current_quantity} {materialData?.uom}
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-500">Loading sites...</div>
            </div>
          ) : selectedSite ? (
            // Action Form
            <div className="space-y-4">
              <button
                onClick={handleCancel}
                className="text-sm text-[#3B0069] hover:underline mb-2"
              >
                ← Back to sites
              </button>

              <div className="bg-[#F9F9F9] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 size={16} className="text-[#3B0069]" />
                  <span className="font-medium text-[14px] text-[#232323]">
                    {selectedSite.site_name}
                  </span>
                </div>
                <p className="text-[12px] text-gray-500">
                  {selectedSite.address && `${selectedSite.address}, `}
                  {selectedSite.city}
                </p>
                {getSiteMaterial(selectedSite.id) && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <span className="text-[12px] text-gray-500">
                      Current Stock:{" "}
                    </span>
                    <span className="font-medium text-[14px] text-[#232323]">
                      {getSiteMaterial(selectedSite.id).current_stock}{" "}
                      {materialData?.uom}
                    </span>
                  </div>
                )}
              </div>

              <div className="bg-[#ECE6F0] rounded-lg p-4 flex items-center gap-3">
                {action === "send" ? (
                  <ArrowUpRight size={24} className="text-[#3B0069]" />
                ) : (
                  <ArrowDownRight size={24} className="text-[#3B0069]" />
                )}
                <div>
                  <div className="font-semibold text-[16px] text-[#232323]">
                    {action === "send" ? "Send to Site" : "Receive from Site"}
                  </div>
                  <div className="text-[14px] text-gray-600">
                    {action === "send"
                      ? "Transfer material from warehouse to site"
                      : "Transfer material from site to warehouse"}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-[14px] font-medium text-[#232323] mb-2">
                  Quantity ({materialData?.uom})
                </label>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder={`Enter quantity in ${materialData?.uom}`}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3B0069] focus:border-transparent"
                  min="0"
                  step="0.01"
                />
                {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleCancel}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="flex-1 px-4 py-2 bg-[#3B0069] text-white rounded-lg hover:bg-[#2d0051] transition"
                >
                  Confirm {action === "send" ? "Send" : "Receive"}
                </button>
              </div>
            </div>
          ) : (
            // Sites List
            <div className="space-y-3">
              <h3 className="font-medium text-[14px] text-gray-600 mb-4">
                Select a site ({sites.length} sites)
              </h3>

              {sites.map((site) => {
                const siteMaterial = getSiteMaterial(site.id);
                const hasStock = siteMaterial && siteMaterial.current_stock > 0;

                return (
                  <div
                    key={site.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-[#3B0069] transition"
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-[#ECE6F0] flex items-center justify-center flex-shrink-0">
                        <Building2 size={16} className="text-[#3B0069]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-[14px] text-[#232323] truncate">
                          {site.site_name}
                        </h4>
                        <p className="text-[12px] text-gray-500 truncate">
                          {site.address && `${site.address}, `}
                          {site.city || "No address"}
                        </p>
                      </div>
                    </div>

                    <div className="mb-3 pb-3 border-b border-gray-200">
                      {siteMaterial ? (
                        <div className="flex items-center justify-between">
                          <span className="text-[12px] text-gray-500">
                            Available Stock
                          </span>
                          <span className="font-medium text-[14px] text-[#232323]">
                            {siteMaterial.current_stock} {materialData?.uom}
                          </span>
                        </div>
                      ) : (
                        <div className="text-[12px] text-amber-600 bg-amber-50 px-2 py-1 rounded">
                          Material not available at this site
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAction(site, "send")}
                        className="flex-1 px-3 py-1.5 text-[14px] border border-[#3B0069] rounded-lg text-[#3B0069] bg-[#F9F5FC] hover:bg-[#ECE6F0] transition flex items-center justify-center gap-1"
                      >
                        <ArrowUpRight size={14} />
                        Send
                      </button>
                      <button
                        onClick={() => handleAction(site, "receive")}
                        disabled={!hasStock}
                        className={`flex-1 px-3 py-1.5 text-[14px] border rounded-lg transition flex items-center justify-center gap-1 ${
                          hasStock
                            ? "border-[#3B0069] text-[#3B0069] bg-[#F9F5FC] hover:bg-[#ECE6F0]"
                            : "border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed"
                        }`}
                      >
                        <ArrowDownRight size={14} />
                        Receive
                      </button>
                    </div>
                  </div>
                );
              })}

              {sites.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Building2 size={48} className="mx-auto mb-3 text-gray-300" />
                  <p>No sites found</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SendReceiveSideBar;
